import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/PasswordInput";
import { MailCheck, CheckCircle2, ArrowLeft, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/134878/pexels-photo-134878.jpeg";

const AuthShell = ({ children }) => (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="relative hidden lg:block">
      <img src={HERO} alt="Farm" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1E2B22]/90 via-[#1E2B22]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 p-12">
        <Logo size={60} className="mb-4 ring-2 ring-white/30" />
        <h2 className="font-display text-4xl font-extrabold tracking-tight text-white">Modav Farms Internal System</h2>
        <p className="mt-3 max-w-md text-white/70">Secure, employee-only management for your farm operations.</p>
      </div>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-sm fade-up">{children}</div>
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="forgot-sent">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
            <MailCheck className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Check your email</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            If an account exists for <b>{email}</b>, we've sent a link to reset your password. The link expires in 1 hour.
          </p>
          <Link to="/login"><Button variant="outline" className="mt-6 w-full" data-testid="forgot-back-to-login">Back to sign in</Button></Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Link to="/login" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to sign in
      </Link>
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Forgot password</h1>
      <p className="mt-2 text-sm text-muted-foreground">Enter your work email and we'll send you a reset link.</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            required data-testid="forgot-email" className="mt-1.5" placeholder="you@modavfarms.com" />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit">
          {busy ? "Sending…" : "Send reset link"}
        </Button>
      </form>
    </AuthShell>
  );
}

export function ForcePasswordChange() {
  const { user, checkAuth, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("New password must be at least 6 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: password });
      toast.success("Password updated. Welcome aboard!");
      await checkAuth();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50">
        <ShieldCheck className="h-6 w-6 text-emerald-600" />
      </div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Set your password</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Welcome, <b>{user?.name}</b>. For security, please replace your temporary password before continuing.
      </p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <Label htmlFor="cur">Temporary password</Label>
          <PasswordInput id="cur" value={current} onChange={(e) => setCurrent(e.target.value)}
            required data-testid="change-current" className="mt-1.5" placeholder="From your welcome email" />
        </div>
        <div>
          <Label htmlFor="np2">New password</Label>
          <PasswordInput id="np2" value={password} onChange={(e) => setPassword(e.target.value)}
            required data-testid="change-new" className="mt-1.5" placeholder="At least 6 characters" />
        </div>
        <div>
          <Label htmlFor="cp2">Confirm new password</Label>
          <PasswordInput id="cp2" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            required data-testid="change-confirm" className="mt-1.5" placeholder="Re-enter new password" />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="change-submit">
          {busy ? "Saving…" : "Save & continue"}
        </Button>
      </form>
      <button onClick={logout} className="mt-6 w-full text-center text-sm text-muted-foreground hover:text-foreground" data-testid="change-logout">
        Sign out
      </button>
    </AuthShell>
  );
}


export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="reset-invalid">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Invalid reset link</h1>
          <p className="mt-3 text-sm text-muted-foreground">This link is missing its token. Please request a new password reset.</p>
          <Link to="/forgot-password"><Button variant="outline" className="mt-6 w-full" data-testid="reset-request-new">Request new link</Button></Link>
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="reset-done">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Password updated</h1>
          <p className="mt-3 text-sm text-muted-foreground">Your password has been changed. Redirecting you to sign in…</p>
          <Link to="/login"><Button className="mt-6 w-full" data-testid="reset-go-login">Sign in now</Button></Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Set a new password</h1>
      <p className="mt-2 text-sm text-muted-foreground">Choose a strong password you haven't used before.</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <Label htmlFor="np">New password</Label>
          <PasswordInput id="np" value={password} onChange={(e) => setPassword(e.target.value)}
            required data-testid="reset-password" className="mt-1.5" placeholder="At least 6 characters" />
        </div>
        <div>
          <Label htmlFor="cp">Confirm password</Label>
          <PasswordInput id="cp" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            required data-testid="reset-confirm" className="mt-1.5" placeholder="Re-enter password" />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="reset-submit">
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
    </AuthShell>
  );
}
